/* ⚠️ GENERATED from app/services/vibeStickers.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/vibeStickers.ts — reading the answers off an interactive sticker.
//
// ══ WHAT WAS WRONG ═════════════════════════════════════════════════════════
// 🟥 THE LOVING INDICATOR TOLD YOU NOTHING. You dragged the face, it stayed
//    where you left it, and that was the whole interaction — no sense of where
//    anybody else had put theirs, so the widget asked a question and then
//    refused to answer it. The poll beside it did reveal its split, which made
//    the silence look like a bug rather than a design.
//
// 🟥 AND THE AUTHOR COULD NOT SEE THE ANSWERS AT ALL. Every vote and every
//    drag has always been stored on the post — `votes` keyed by uid for a poll,
//    `values` keyed by uid for a slider — and nothing anywhere read them back
//    as numbers. The person who asked the question was the one person who could
//    not see the result.
//
// ══ THE RULES ══════════════════════════════════════════════════════════════
// 🟢 ANSWER FIRST, THEN SEE THE ANSWERS. Showing the split before you have
//    voted steers the vote; that is why the poll already waited, and it is why
//    the slider now waits too.
// 🟢 THE AUTHOR ALWAYS SEES THEM. They asked; making them vote in their own
//    poll to read it would be a silly toll, and their vote would pollute the
//    result they were trying to read.
// 🟢 ONE TALLY, COMPUTED ONE WAY. The card, the reveal and the author's sheet
//    all count through here, so the number on the widget and the number in the
//    sheet cannot disagree.

/** The two option labels, with the defaults the editor writes. */
export const POLL_FALLBACK = ['Yes', 'No'];

/**
 * 🟢 `??`, NOT `||`. The editor deliberately allows an empty option label —
 *    there is a comment in the composer about exactly this — and `||` would
 *    resurrect "Yes"/"No" over the author's own blank.
 */
export function pollOptions(sticker) {
  const o = sticker?.options;
  return [o?.[0] ?? POLL_FALLBACK[0], o?.[1] ?? POLL_FALLBACK[1]];
}

/**
 * Every response map is uid → answer; a missing map is nobody answering.
 *
 * 🟥 THE VALUE MUST ALREADY BE A NUMBER — no coercion. `Number(null)` is 0 and
 *    `Number('')` is 0, so a coercing read turns a null left behind by a
 *    half-finished write into a vote for the FIRST option. That is not a
 *    hypothetical: a `values`/`votes` map is written by two clients and read by
 *    a third, and a poll silently gaining phantom Yes votes is unfalsifiable
 *    from the outside.
 */
function entries(map) {
  if (!map || typeof map !== 'object') return [];
  const out = [];
  for (const k of Object.keys(map)) {
    const v = map[k];
    if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
  }
  return out;
}
/**
 * 🟥 THE SECOND PERCENTAGE IS `100 - first`, NOT ITS OWN ROUNDING. Rounding
 *    both independently gives 33% / 67% one time and 33% / 66% the next,
 *    depending on the split — a bar that visibly fails to fill its track.
 */
export function pollTally(sticker) {
  let a = 0;
  let b = 0;
  for (const v of entries(sticker?.votes)) {
    if (v === 0) a += 1;else if (v === 1) b += 1;
  }
  const total = a + b;
  const p0 = total === 0 ? 0 : Math.round(a / total * 100);
  return {
    counts: [a, b],
    total,
    pct: [p0, total === 0 ? 0 : 100 - p0]
  };
}
/**
 * 🟥 EVERY VALUE IS CLAMPED ON THE WAY IN. A stored ratio comes from a drag
 *    that was already bounded to the track, but an average is the one number a
 *    single out-of-range write would visibly corrupt — a 6000% "loved it" —
 *    and clamping here costs nothing.
 */
export function sliderTally(sticker) {
  const vals = entries(sticker?.values).map(v => Math.min(1, Math.max(0, v)));
  if (!vals.length) return {
    total: 0,
    avg: 0,
    avgPct: 0
  };
  let sum = 0;
  for (const v of vals) sum += v;
  const avg = sum / vals.length;
  return {
    total: vals.length,
    avg,
    avgPct: Math.round(avg * 100)
  };
}

/** This viewer's own vote, or null when they have not voted. */
export function myPollVote(sticker, uid) {
  if (!uid) return null;
  const v = sticker?.votes?.[uid];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** This viewer's own slider position, or null when they have not dragged it. */
export function mySliderValue(sticker, uid) {
  if (!uid) return null;
  const v = sticker?.values?.[uid];
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return Math.min(1, Math.max(0, v));
}

/** Has this viewer answered this sticker? */
export function hasResponded(sticker, uid) {
  if (!sticker) return false;
  if (sticker.type === 'slider') return mySliderValue(sticker, uid) !== null;
  return myPollVote(sticker, uid) !== null;
}

/**
 * Should this viewer be shown the result?
 *
 * 🟥 THE AUTHOR IS THE POST'S `userId`, INCLUDING FOR A PAGE POST. A page post
 *    keeps the publishing account's uid there, so the person running the page
 *    reads their own page's results without a second rule.
 */
export function revealsResults(sticker, uid, authorUid) {
  if (!sticker) return false;
  if (uid && authorUid && uid === authorUid) return true;
  return hasResponded(sticker, uid);
}

/* ══ WHICH PICTURE A STICKER BELONGS TO ═══════════════════════════════════════
   🟥 A CAROUSEL POST KEPT ONLY THE FIRST PICTURE'S STICKERS — and then drew them
      on EVERY picture. The composer edits each photo separately, but publishing
      stamped the post from `mediaItems[0]` alone: a sticker placed on photo 2
      vanished, and photo 1's sticker reappeared on photos 2, 3 and 4 (the same
      poll four times, each one answering the same question).

   🟢 EVERY PICTURE'S STICKERS ARE KEPT, IN THE ONE `stickers` ARRAY THE ANSWERS
      ARE ALREADY WRITTEN INTO, each tagged with the picture it was placed on.
      The first picture's stickers carry no tag — exactly the shape every post
      before this has — so an old post reads the same, on either client, and
      its stickers now sit only on the photo they were put on. */

/** The picture a sticker sits on: 0 when untagged (every post written before this). */
export function stickerMediaIndex(s) {
  const n = Number(s?.mediaIndex);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

/** The stickers drawn on one picture of a post. */
export function stickersForMedia(post, index = 0) {
  const list = post?.stickers;
  if (!Array.isArray(list) || !list.length) return [];
  const at = Number.isInteger(index) && index > 0 ? index : 0;
  return list.filter(s => s && stickerMediaIndex(s) === at);
}

/**
 * The post's `stickers` array from the composer's per-picture lists.
 * 🟢 Picture 0 stays untagged; the rest carry `mediaIndex`. A stray tag from an
 *    earlier draft is replaced by the slot the sticker is actually in.
 */
export function collectPostStickers(perMedia) {
  const out = [];
  (perMedia || []).forEach((list, i) => {
    for (const st of Array.isArray(list) ? list : []) {
      if (!st || typeof st !== 'object') continue;
      const {
        mediaIndex: _drop,
        ...rest
      } = st;
      out.push(i > 0 ? {
        ...rest,
        mediaIndex: i
      } : rest);
    }
  });
  return out;
}

/** The poll and slider stickers on a post, in the order the author placed them. */
export function interactiveStickers(post) {
  const list = post?.stickers;
  if (!list || !list.length) return [];
  return list.filter(s => s && (s.type === 'poll' || s.type === 'slider'));
}
/**
 * The author's read-out, one row per interactive sticker.
 *
 * 🟢 THE PLACEHOLDER BECOMES THE HEADING WHEN THE AUTHOR LEFT THE QUESTION
 *    BLANK — a row titled with an empty string is unreadable, and the widget
 *    on the video is already showing that same placeholder text.
 */
export function stickerResults(post) {
  return interactiveStickers(post).map((s, i) => {
    const id = String(s.id || i);
    if (s.type === 'slider') {
      const t = sliderTally(s);
      return {
        id,
        kind: 'slider',
        question: s.question || 'Ask anything...',
        total: t.total,
        options: [],
        avgPct: t.avgPct
      };
    }
    const t = pollTally(s);
    const labels = pollOptions(s);
    return {
      id,
      kind: 'poll',
      question: s.question || 'Ask a question...',
      total: t.total,
      options: [{
        label: labels[0],
        count: t.counts[0],
        pct: t.pct[0]
      }, {
        label: labels[1],
        count: t.counts[1],
        pct: t.pct[1]
      }],
      avgPct: 0
    };
  });
}

/** How many people answered anything on this post — the button's badge. */
export function totalResponses(post) {
  let n = 0;
  for (const r of stickerResults(post)) n += r.total;
  return n;
}

/**
 * Should the author's results button be on this reel?
 *
 * 🟥 ASKED OF THE POST, NOT OF THE COUNTS. A button that appears only once
 *    somebody has answered is a button the author cannot find while waiting for
 *    the first answer — and its absence reads as "this post has no poll".
 *    It shows whenever there is something to report on, and reports zero.
 */
export function showsAuthorResults(post, uid) {
  if (!post || !uid) return false;
  if (post.userId !== uid) return false;
  return interactiveStickers(post).length > 0;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANSWERING — ONE RULE, RUN BY THE SERVER AND MIRRORED BY BOTH CLIENTS.

   🟥 THE ANSWER NEVER REACHED THE POST. Both clients wrote `stickers` straight
      onto posts/{id}, and the rules let a stranger write only likes, comments,
      shares and counters — so every viewer's slide and vote was refused, the
      error swallowed, and the only copy was the one in memory on that screen.
      Leave the photo, come back to a fresh copy of the post: "it shows I need to
      slide it again". (The author's own answer did save, which is why it looked
      intermittent.) It cannot simply be added to the rules: `stickers` is an
      array of maps, and the permission to add your own answer is the
      permission to rewrite everybody's — the same reason Vibes polls vote
      through the server (vibePolls.js).

   🟢 SO AN ANSWER GOES THROUGH /api/vibes/sticker/answer, which runs this
      function in a transaction. The clients run the same function to paint the
      answer at once.

   🟢 AND AN ANSWER IS FINAL. A viewer sees the widget fresh until THEY answer;
      the answer then shows the crowd's result. Re-sliding after seeing the
      average would let anyone steer it, so a second answer is refused — here,
      on the server, and by the widgets.
   ═══════════════════════════════════════════════════════════════════════════ */

export function applyStickerAnswer(stickers, stickerId, uid, value) {
  if (!uid) return {
    ok: false,
    error: 'no_user'
  };
  const list = Array.isArray(stickers) ? stickers : [];
  const i = list.findIndex(s => !!s && String(s.id) === String(stickerId));
  if (i < 0) return {
    ok: false,
    error: 'no_such_sticker'
  };
  const s = list[i];
  const kind = s.type === 'slider' ? 'slider' : s.type === 'poll' ? 'poll' : null;
  if (!kind) return {
    ok: false,
    error: 'not_interactive'
  };
  if (typeof value !== 'number' || !Number.isFinite(value)) return {
    ok: false,
    error: 'bad_value'
  };
  let v = value;
  if (kind === 'poll') {
    if (v !== 0 && v !== 1) return {
      ok: false,
      error: 'bad_value'
    };
  } else v = Math.min(1, Math.max(0, v));
  if (hasResponded(s, uid)) return {
    ok: false,
    error: 'already_answered'
  };
  const next = list.slice();
  next[i] = kind === 'slider' ? {
    ...s,
    values: {
      ...(s.values || {}),
      [uid]: v
    }
  } : {
    ...s,
    votes: {
      ...(s.votes || {}),
      [uid]: v
    }
  };
  return {
    ok: true,
    stickers: next,
    kind,
    value: v
  };
}

/* ── THE ANSWERS THIS DEVICE HAS GIVEN, FOR THIS SESSION ──────────────────────
   🟥 THE SAME POST LIVES IN MANY LISTS — the feed, the friends feed, a profile,
      the reels, the full-screen viewer — each holding its own copy, and only
      some of them were patched when you answered. Re-open the photo from a list
      that was not patched and your answer was gone until a refetch.
   🟢 So the answer is also remembered here, by post and sticker, and every
      widget reads its sticker THROUGH this. Whatever copy of the post it was
      handed, an answer given on this device shows. */
const _myAnswers = new Map();
const _answerKey = (postId, stickerId) => `${String(postId || '')}|${String(stickerId || '')}`;
export function rememberMyAnswer(postId, stickerId, value) {
  if (!postId || !stickerId || typeof value !== 'number' || !Number.isFinite(value)) return;
  if (_myAnswers.size > 2000) _myAnswers.clear(); // bounded; it is only a session cache
  _myAnswers.set(_answerKey(postId, stickerId), value);
}
export function myRememberedAnswer(postId, stickerId) {
  const v = _myAnswers.get(_answerKey(postId, stickerId));
  return typeof v === 'number' ? v : null;
}

/** The sticker as THIS viewer should see it: its stored answers plus mine from this device. */
export function withMyAnswer(sticker, postId, uid) {
  if (!sticker || !uid || hasResponded(sticker, uid)) return sticker;
  const v = myRememberedAnswer(postId, sticker.id);
  if (v === null) return sticker;
  return sticker.type === 'slider' ? {
    ...sticker,
    values: {
      ...(sticker.values || {}),
      [uid]: v
    }
  } : {
    ...sticker,
    votes: {
      ...(sticker.votes || {}),
      [uid]: v
    }
  };
}

/** For tests. */
export function _resetMyAnswers() {
  _myAnswers.clear();
}
